import { Loader2 } from 'lucide-react'

/**
 * Button — gestandaardiseerde knop (R11 design system).
 * Wrapt de echte index.css-classes btn-primary / btn-ghost / btn-danger (B0).
 *
 * Props:
 *   children      — label (optioneel; icon-only knop mag, geef dan aria-label mee)
 *   variant       — 'primary' | 'ghost' | 'danger' (default 'primary')
 *   size          — 'md' (btn-* default) | 'sm' (compacter)
 *   icon          — Lucide-component (optioneel)
 *   iconPosition  — 'left' (default) | 'right'
 *   loading       — bool; toont spinner + disabled
 *   disabled      — bool
 *   type          — button-type (default 'button')
 *   as            — element/component, bv. react-router Link (default 'button')
 *   className     — extra classes (optioneel)
 *   ...rest       — doorgegeven (onClick, to, aria-label, ...)
 *
 * De btn-* classes leveren al inline-flex + gap-2, dus icon + label spatiëren
 * vanzelf. Size 'sm' overschrijft de btn-* padding met !-utilities.
 */

const VARIANT = {
  primary: 'btn-primary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
}

const SIZE = {
  md: '',
  sm: '!px-3 !py-1.5 !text-xs',
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  type = 'button',
  as: Tag = 'button',
  className = '',
  ...rest
}) {
  const variantClass = VARIANT[variant] ?? VARIANT.primary
  const sizeClass = SIZE[size] ?? ''
  const isDisabled = disabled || loading

  const content = (
    <>
      {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
      {!loading && Icon && iconPosition === 'left' && <Icon className="w-4 h-4" aria-hidden="true" />}
      {children != null && <span>{children}</span>}
      {!loading && Icon && iconPosition === 'right' && <Icon className="w-4 h-4" aria-hidden="true" />}
    </>
  )

  // Niet-button element (bv. <Link>): geen type/disabled-attribuut.
  if (Tag !== 'button') {
    return (
      <Tag
        className={`${variantClass} ${sizeClass} ${isDisabled ? 'opacity-60 pointer-events-none' : ''} ${className}`}
        aria-disabled={isDisabled || undefined}
        {...rest}
      >
        {content}
      </Tag>
    )
  }

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={`${variantClass} ${sizeClass} ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
      {...rest}
    >
      {content}
    </button>
  )
}
